/**
 * Global PropertySelector (Phase 1 redesign).
 *
 * A searchable combobox that queries `/api/properties/search` and lets the
 * user switch the app's active property. Also exposes a persistent
 * "All Properties" toggle for the master portfolio experience.
 *
 * Non-invasive — it delegates state ownership to App.js via props:
 *   - selected              : current selectedProperty object (or null)
 *   - onSelect(propertyObj) : callback when the user picks a property
 *   - allPropertiesMode     : boolean from usePropertyContext
 *   - onToggleAllMode(bool) : callback when the user toggles master mode
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PropertySelector({
  selected,
  onSelect,
  allPropertiesMode = false,
  onToggleAllMode,
  showMasterToggle = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [otaTags, setOtaTags] = useState({});
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // OTA linkage tags (property_id -> ["MMT","Agoda","Yatra"]).
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/properties/ota-tags`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setOtaTags(r?.data?.tags || {});
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  const otaBadgeClass = (ota) => ({
    MMT: 'bg-red-100 text-red-700',
    Agoda: 'bg-blue-100 text-blue-700',
    Yatra: 'bg-emerald-100 text-emerald-700',
  }[ota] || 'bg-gray-100 text-gray-700');

  const renderOtaTags = (pid) => {
    const tags = otaTags[pid] || [];
    if (tags.length === 0) return null;
    return (
      <span className="flex flex-wrap gap-1 mt-0.5" data-testid={`property-ota-tags-${pid}`}>
        {tags.map((t) => (
          <span key={t} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${otaBadgeClass(t)}`} data-testid={`property-ota-tag-${pid}-${t}`}>{t}</span>
        ))}
      </span>
    );
  };

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        // Fetch more properties by default so that vertical scrolling inside
        // the dropdown becomes meaningful for orgs with many hotels. Display-
        // only change — the underlying `/properties/search` behaviour and any
        // scoping/permission logic remain unchanged.
        const url = query.trim()
          ? `${API}/properties/search?q=${encodeURIComponent(query.trim())}&limit=100`
          : `${API}/properties/search?limit=100`;
        const r = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setResults(r?.data?.results || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePick = (prop) => {
    if (onToggleAllMode) onToggleAllMode(false);
    if (onSelect) onSelect(prop);
    setOpen(false);
    setQuery('');
  };

  const handleAllProperties = () => {
    if (onToggleAllMode) onToggleAllMode(true);
    setOpen(false);
    setQuery('');
  };

  const label = allPropertiesMode
    ? 'All Properties'
    : (selected ? `${selected.grb_id ? selected.grb_id + ' · ' : ''}${selected.name}` : 'Select property…');

  return (
    <div ref={containerRef} className="relative" data-testid="property-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 min-w-[220px] justify-between"
        data-testid="property-selector-trigger"
      >
        <span className="truncate">{label}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-2 w-[360px] rounded-md border border-gray-200 bg-white shadow-lg flex flex-col max-h-[70vh]"
          data-testid="property-selector-panel"
        >
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, GRB ID, city, owner, phone…"
              className="w-full px-2 py-1.5 text-sm border rounded outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="property-selector-input"
            />
          </div>
          {showMasterToggle && (
            <button
              type="button"
              onClick={handleAllProperties}
              className={`w-full text-left px-3 py-2 text-sm border-b hover:bg-gray-50 shrink-0 ${
                allPropertiesMode ? 'bg-orange-50 font-semibold text-orange-700' : ''
              }`}
              data-testid="property-selector-all-btn"
            >
              🏨 All Properties <span className="text-xs text-gray-500">— portfolio view</span>
            </button>
          )}
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            data-testid="property-selector-results"
          >
            {loading && (
              <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
            )}
            {!loading && results.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-500">No properties match.</div>
            )}
            {!loading && results.map((p) => {
              const isSelected = selected && selected.id === p.id && !allPropertiesMode;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePick(p)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    isSelected ? 'bg-orange-50' : ''
                  }`}
                  data-testid={`property-selector-option-${p.id}`}
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-800">{p.name}</div>
                      <div className="truncate text-[11px] text-gray-500">
                        {p.city}{p.city && p.state ? ', ' : ''}{p.state || ''}
                        {p.owner_name ? ` · ${p.owner_name}` : ''}
                      </div>
                      {renderOtaTags(p.id)}
                    </div>
                    {p.grb_id && (
                      <span className="shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {p.grb_id}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
